// @ts-nocheck
import React, { FC, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { applyActionCode, onAuthStateChanged, sendEmailVerification } from "firebase/auth";
import { whichAuth as getAuth } from './getAuth';
import { alertState, showChangePasswordState, userState, viewState, offerState } from './atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  FIREBASE_API_KEY,
  FIREBASE_APP_ID,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_MEASUREMENT_ID,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
  REST_API_URL,
  viewBills,
  viewDashboard,
  viewPayments,
  viewProviders,
  viewOffers,
  viewFuel,
  viewSettings
} from './constants/global';
import {
  Authentication,
  ChangePassword,
  InvoiceRedirect,
  Layout,
  PoA,
  AlertModal,
} from './components';
import Dashboard from './pages/Dashboard';
import Providers from './pages/Providers';
import Payments from './pages/Payments';
import Settings from './pages/Settings';
import {search as userSearch} from './components/UserSearch'
import Bills from './pages/Bills';
import Offers from './pages/Offers';
import Fuel from './pages/Fuel';
import { getOffers } from './pages/Offers';
import { handleErrors } from './utils/handleErrors';
import { IonApp, IonContent, setupIonicReact } from '@ionic/react';


/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

/* Global styles */
import './theme/global.css'
import { setFirebaseApp } from './getAuth';

setupIonicReact({
  mode: 'md'
});

const app = initializeApp({
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
  measurementId: FIREBASE_MEASUREMENT_ID
});
setFirebaseApp(app);
  
const App: FC = () => {
  const [initialising, setInitialising] = useState<boolean>(true);
  const user = useRecoilValue(userState);
  const setUser = useSetRecoilState(userState);
  const setOffers = useSetRecoilState(offerState);
  const view = useRecoilValue(viewState);
  const alertData = useRecoilValue(alertState);
  const setAlertData = useSetRecoilState(alertState);
  const setView = useSetRecoilState(viewState);
  const setShowChangePassword = useSetRecoilState(showChangePasswordState);
  
  // required to allow Android apps to close on back button click (or swipe)
  document.addEventListener('ionBackButton', (ev: BackButtonEvent) => ev.detail.register(-1, () => {
    if([viewDashboard,viewBills,viewPayments,viewProviders,viewOffers,viewFuel,viewSettings].includes(view)) {
      navigator.app.exitApp();
    }
  }));

  const syncBackendUser = async firebaseUser => {
    try {
      setView(viewDashboard);
      await userSearch(firebaseUser.uid)
        .then(backendUser => {
          if(backendUser) {
            setUser({
              ...JSON.parse(JSON.stringify(firebaseUser)),
              ...backendUser
            });
            setInitialising(false);
            backendUser.admin && setView(viewBills);
          } else {
            sendEmailVerification(firebaseUser);
            getAuth().currentUser.getIdToken().then(token =>
              fetch(REST_API_URL + '/users', {
                method: 'POST',
                body: JSON.stringify({
                  email: firebaseUser.email,
                  firebaseId: firebaseUser.uid
                }),
                headers: {
                  'Authorization': 'Bearer ' + token,
                  'Content-Type': 'application/json'
                }
              })
              .then(handleErrors)
              .then(() => syncBackendUser(firebaseUser))
              .catch(() => setAlertData({
                title:"Failed to load your profile",
                subTitle:"An error occurred loading your profile. Please try again or contact us."
              })));
          }})
    } catch(error) {
      setInitialising(false);
      setAlertData({
        title:"Failed to load your profile",
        subTitle:"An error occurred loading your profile. Please try again or contact us."
      });
    }
  }
  
  const firebaseAuthStateChanged = firebaseUser => {
    setInitialising(true);
    const mode = new URLSearchParams(window.location.search).get('mode');
    const oobCode = new URLSearchParams(window.location.search).get('oobCode');
    if (firebaseUser) {
      // user signed in
      if('verifyEmail' === mode && oobCode && !firebaseUser.emailVerified) {
        applyActionCode(getAuth(), oobCode)
          .then(() => syncBackendUser({
            ...firebaseUser,
            emailVerified: true
          }))
          .catch(() => syncBackendUser(firebaseUser));
      } else {
        syncBackendUser(firebaseUser);
      }
    } else if('resetPassword' === mode) {
      setInitialising(false);
      setShowChangePassword(true);
    } else {
      setInitialising(false);
    }
  }

  useEffect(() => onAuthStateChanged(getAuth(), firebaseAuthStateChanged), []);
  
  useEffect(() => user && getOffers(user).then(offers => setOffers(offers)), [user]);
  
  return (
    <IonApp>
      <Layout>
        { !user ? <Authentication initialising={initialising} /> :
          <IonContent>
            <Dashboard show={view === viewDashboard} />
            <Providers show={view === viewProviders} />
            <Payments show={view === viewPayments} />
            <Settings show={view === viewSettings} />
            <Bills show={view === viewBills} />
            <Offers show={view === viewOffers} />
            <Fuel show={view === viewFuel} />
          </IonContent>
        }
        <ChangePassword />
        {user && <InvoiceRedirect /> }
        {user && <PoA /> }

        <AlertModal {...alertData}
          onClosed={() => setAlertData({
            isOpen: false,
            title: '',
            onConfirm: null,
            onCancel: null,
            onClosed: null,
            subTitle: '',
            textButton: 'Ok',
            showCancelButton: false
          })} 
        />
      </Layout>
    </IonApp>
  )}

export default App;
